import {
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { Icone } from './Icone.tsx';

type Base = {
  rotulo?: string;
  dica?: string;
  erro?: string | null;
  obrigatorio?: boolean;
  /** Marca o campo como opcional no rótulo, em vez de deixar ambíguo. */
  opcional?: boolean;
  /** Conteúdo à esquerda dentro da casca — ícone, "R$", etc. */
  prefixo?: ReactNode;
  sufixo?: ReactNode;
};

/**
 * Envolve rótulo, casca, dica e erro. Cada campo gera os ids de dica e erro e
 * os liga por `aria-describedby`, para o leitor de tela anunciar os dois.
 */
function Envolucro({
  id,
  rotulo,
  dica,
  erro,
  obrigatorio,
  opcional,
  contador,
  children,
}: Base & { id: string; contador?: ReactNode; children: ReactNode }) {
  return (
    <div className="campo">
      {rotulo && (
        <label className="campo__rotulo" htmlFor={id}>
          {rotulo}
          {obrigatorio && (
            <span className="campo__obrigatorio" aria-hidden="true">
              *
            </span>
          )}
          {opcional && <span className="campo__opcional">opcional</span>}
        </label>
      )}

      {children}

      {(dica || erro || contador) && (
        <div className="linha" style={{ gap: 'var(--gap-2)', alignItems: 'flex-start' }}>
          {erro ? (
            <span className="campo__erro" id={`${id}-erro`} role="alert">
              <Icone.Alerta tamanho={13} />
              {erro}
            </span>
          ) : (
            dica && (
              <span className="campo__dica" id={`${id}-dica`}>
                {dica}
              </span>
            )
          )}
          {contador}
        </div>
      )}
    </div>
  );
}

function descritores(id: string, dica?: string, erro?: string | null) {
  const partes = [dica ? `${id}-dica` : null, erro ? `${id}-erro` : null].filter(Boolean);
  return partes.length ? partes.join(' ') : undefined;
}

type PropsCampo = Base & Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'>;

export function Campo({
  rotulo,
  dica,
  erro,
  obrigatorio,
  opcional,
  prefixo,
  sufixo,
  id: idExterno,
  ...props
}: PropsCampo) {
  const idGerado = useId();
  const id = idExterno ?? idGerado;

  return (
    <Envolucro {...{ id, rotulo, dica, erro, obrigatorio, opcional }}>
      <div className={`campo__casca${erro ? ' campo__casca--erro' : ''}`}>
        {prefixo && <span className="campo__afixo">{prefixo}</span>}
        <input
          id={id}
          className="campo__entrada"
          aria-required={obrigatorio}
          aria-invalid={!!erro}
          aria-describedby={descritores(id, dica, erro)}
          {...props}
        />
        {sufixo && <span className="campo__afixo">{sufixo}</span>}
      </div>
    </Envolucro>
  );
}

type PropsArea = Base &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'prefix'> & { maximo?: number };

export function CampoArea({
  rotulo,
  dica,
  erro,
  obrigatorio,
  opcional,
  maximo,
  id: idExterno,
  value,
  ...props
}: PropsArea) {
  const idGerado = useId();
  const id = idExterno ?? idGerado;
  const usado = typeof value === 'string' ? value.length : 0;

  return (
    <Envolucro
      {...{ id, rotulo, dica, erro, obrigatorio, opcional }}
      contador={
        maximo ? (
          <span
            className="campo__contador"
            style={usado > maximo ? { color: 'var(--erro)' } : undefined}
          >
            {usado}/{maximo}
          </span>
        ) : undefined
      }
    >
      <div className={`campo__casca campo__casca--area${erro ? ' campo__casca--erro' : ''}`}>
        <textarea
          id={id}
          className="campo__entrada"
          aria-required={obrigatorio}
          aria-invalid={!!erro}
          aria-describedby={descritores(id, dica, erro)}
          maxLength={maximo}
          value={value}
          {...props}
        />
      </div>
    </Envolucro>
  );
}

/** Campo de busca com ícone e botão de limpar quando há texto. */
export function CampoBusca({
  valor,
  aoMudar,
  placeholder = 'Buscar',
  rotuloAcessivel = 'Buscar',
}: {
  valor: string;
  aoMudar: (v: string) => void;
  placeholder?: string;
  rotuloAcessivel?: string;
}) {
  return (
    <div className="campo__casca crescer">
      <span className="campo__afixo">
        <Icone.Busca tamanho={16} />
      </span>
      <input
        type="search"
        className="campo__entrada"
        value={valor}
        placeholder={placeholder}
        aria-label={rotuloAcessivel}
        onChange={(e) => aoMudar(e.target.value)}
      />
      {valor && (
        <button
          type="button"
          className="campo__afixo"
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          onClick={() => aoMudar('')}
          aria-label="Limpar busca"
        >
          <Icone.Fechar tamanho={15} />
        </button>
      )}
    </div>
  );
}
